import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { fetchEmailIntake } from "@/lib/emailIntake";
import { useDisbursements } from "@/hooks/useDisbursements";
import { isMine, submittedRows } from "@/lib/submittedInvoices";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const day = (value) => {
  const [y, m, d] = String(value || "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};
const PAID_STYLE = {
  paid: "border-emerald-200 bg-emerald-50 text-emerald-800",
  part: "border-violet-200 bg-violet-50 text-violet-800",
  open: "border-slate-200 bg-slate-50 text-slate-700",
};

// The fee slips, invoices, disbursements and travel claims a firm's staff sent in, and
// whether each has been paid (src/lib/submittedInvoices.js). View only.
export default function SubmittedInvoices({ brandType = "lacw" }) {
  const { user } = useAuth();
  const disbursements = useDisbursements(brandType, `${String(brandType).toUpperCase()} Disbursements`);
  const [intake, setIntake] = useState([]);
  const [state, setState] = useState("loading");
  const [scope, setScope] = useState("mine");
  const [paidFilter, setPaidFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setState("loading");
    try {
      setIntake(await fetchEmailIntake(brandType));
      setState("ready");
    } catch {
      setState("error");
    }
  };
  useEffect(() => { load(); }, [brandType]);

  const rows = useMemo(
    () => submittedRows({ intake, disbursements: disbursements.data.items }),
    [intake, disbursements.data.items],
  );
  const mine = useMemo(() => rows.filter((row) => isMine(row, user || {})), [rows, user]);
  const scoped = scope === "mine" ? mine : rows;
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return scoped.filter((row) => {
      if (paidFilter === "paid" && row.paid !== "paid") return false;
      if (paidFilter === "open" && row.paid === "paid") return false;
      return !needle || `${row.clientName} ${row.from} ${row.reference} ${row.kind}`.toLowerCase().includes(needle);
    });
  }, [scoped, paidFilter, search]);
  const paidCount = scoped.filter((row) => row.paid === "paid").length;

  const chip = (active, onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-semibold ${active ? "border-purple-600 bg-purple-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-purple-300"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <Card className="shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 border-b border-slate-100 py-4">
          <div>
            <CardTitle className="text-lg font-bold text-slate-900">Submitted invoices</CardTitle>
            <p className="mt-0.5 text-sm text-slate-500">
              Fee slips, invoices, disbursements and travel claims sent in, and whether each has been paid.
            </p>
          </div>
          <button type="button" onClick={() => { load(); disbursements.reload(); }} aria-label="Refresh" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-purple-700">
            <RefreshCw className="h-4 w-4" />
          </button>
        </CardHeader>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {chip(scope === "mine", () => setScope("mine"), `Sent by me (${mine.length})`)}
            {chip(scope === "all", () => setScope("all"), `Everyone (${rows.length})`)}
            <span className="mx-1 h-5 w-px bg-slate-200" />
            {chip(paidFilter === "all", () => setPaidFilter("all"), "All")}
            {chip(paidFilter === "paid", () => setPaidFilter("paid"), `Paid (${paidCount})`)}
            {chip(paidFilter === "open", () => setPaidFilter("open"), `Not yet paid (${scoped.length - paidCount})`)}
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search client, sender, invoice" aria-label="Search submitted invoices" className="ml-auto h-8 w-full text-xs sm:w-64" />
          </div>

          {state === "error" && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">The emailed invoices could not be loaded. Refresh to try again.</p>}
          {disbursements.loadError && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">Disbursements could not be loaded, so their payment status is not shown.</p>}

          {state === "loading" && !rows.length ? (
            <p className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          ) : visible.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">
              {scope === "mine" && !mine.length
                ? `Nothing sent in from ${user?.email || "your address"} yet. "Everyone" shows the whole firm's.`
                : "Nothing matches."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">Received</th>
                    <th className="py-2 pr-3">Client</th>
                    <th className="py-2 pr-3">What</th>
                    <th className="py-2 pr-3">Sent by</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visible.map((row) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{day(row.receivedAt)}</td>
                      <td className="py-2 pr-3 font-semibold text-slate-900">{row.clientName}</td>
                      <td className="max-w-[260px] py-2 pr-3 text-slate-600">
                        <span className="font-semibold text-slate-700">{row.kind}</span>
                        {row.reference && <span className="block truncate text-[11px] text-slate-500" title={row.reference}>{row.reference}</span>}
                      </td>
                      <td className="py-2 pr-3 text-slate-600" title={row.fromEmail}>{row.from}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold text-slate-800">{row.amount ? money(row.amount) : "—"}</td>
                      <td className="py-2">
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${PAID_STYLE[row.paid]}`}>
                          {row.statusText}{row.paid === "paid" && row.paidOn ? ` ${day(row.paidOn)}` : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
